import { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { formatHours } from '../../../helpers/format-time-track.helper';
import { ApprovalHoursSplit } from '../../../types/time-tracking-approvals';

/** The `t` function bound to the time-tracking namespace, threaded into each card. */
export type TranslateFn = ReturnType<typeof useTranslation<'authenticated.timeTracking'>>['t'];

/** Shared card shell for the confirm modal's per-selection cards (project / member / entry). */
export function ApprovalConfirmCard({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-[10px] border border-input bg-muted/40 px-[18px] py-4">{children}</div>
  );
}

/** Card header: a leading marker (color dot or avatar) plus the entity name. */
export function ApprovalConfirmCardHeader({
  marker,
  name,
}: {
  marker: ReactNode;
  name: ReactNode;
}) {
  return (
    <div className="mb-3 flex items-center gap-2">
      {marker}
      <span className="text-[15px] font-bold leading-tight">{name}</span>
    </div>
  );
}

/** A small round project color dot used as a card marker. */
export function ProjectColorDot({ color }: { color: string }) {
  return (
    <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: color }} />
  );
}

/** Label + value line inside the aggregate (project/member) cards. */
export function ProjectMetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-1 text-[13.5px] leading-snug">
      <span className="flex-shrink-0 text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground">{children}</span>
    </div>
  );
}

/**
 * The two approved-hours lines every aggregate (project/member) card shows: what the action
 * writes on the invoice side and on the bill side. They differ whenever a covered entry carries
 * a pending inline edit on only one of them, so the pair is always rendered — never one figure
 * standing in for both.
 */
export function ApprovedHoursSplitRows({
  hours,
  ns,
  t,
}: {
  hours: ApprovalHoursSplit;
  ns: string;
  t: TranslateFn;
}) {
  return (
    <>
      <ProjectMetaRow
        label={t(`${ns}.projectCard.approvedForInvoiceHours` as Parameters<typeof t>[0])}
      >
        {formatHours(hours.invoice)}
      </ProjectMetaRow>
      <ProjectMetaRow
        label={t(`${ns}.projectCard.approvedForBillHours` as Parameters<typeof t>[0])}
      >
        {formatHours(hours.bill)}
      </ProjectMetaRow>
    </>
  );
}

/** Label + value line inside the single-entry detail card. */
export function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-1 text-sm">
      <span className="flex-shrink-0 text-muted-foreground">{label}</span>
      <span className="font-semibold">{children}</span>
    </div>
  );
}
